export function formatCurrency(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatDate(dateStr: string) {
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
}

/**
 * Data de hoje no fuso horário local, no formato YYYY-MM-DD.
 * NUNCA use `new Date().toISOString()` pra isso: toISOString() converte pra UTC,
 * e no horário de Brasília (UTC-3) qualquer coisa lançada depois das 21h já
 * cai no dia seguinte em UTC — a data "hoje" viraria "amanhã" sem querer.
 */
export function todayISO() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Retorna dias até a data (negativo se já passou). */
export function daysUntil(dateStr: string): number {
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = Date.UTC(y, m - 1, d);
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((target - todayUtc) / 86_400_000);
}

export const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

export const WEEKDAY_NAMES_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

/** month é 1-12 */
export function monthLabel(year: number, month: number) {
  return `${MONTH_NAMES[month - 1]} de ${year}`;
}

/** SQLite CURRENT_TIMESTAMP vem como "YYYY-MM-DD HH:MM:SS" (UTC, sem indicador de fuso); ISO já vem com T/Z. */
export function parseServerDate(value: string): Date {
  return value.includes("T") ? new Date(value) : new Date(value.replace(" ", "T") + "Z");
}

export function formatDateTime(value: string): string {
  return parseServerDate(value).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Dias inteiros desde a data (0 = hoje). */
export function daysSince(value: string): number {
  const ms = Date.now() - parseServerDate(value).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/** Aplica máscara progressiva enquanto o usuário digita: (DD) XXXX-XXXX / (DD) 9XXXX-XXXX. */
export function maskPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/** Aplica máscara progressiva de CPF (XXX.XXX.XXX-XX) ou, a partir do 12º dígito, CNPJ (XX.XXX.XXX/XXXX-XX). */
export function maskCpfCnpjInput(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  if (digits.length <= 11) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/** Formata dígitos de telefone BR como (DD) 9XXXX-XXXX ou (DD) XXXX-XXXX. */
export function formatPhoneBR(value: string | null): string {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return value;
}

export function formatCurrencyCompact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1000) {
    return `${sign}${(abs / 1000).toFixed(1).replace(".", ",")}k`;
  }
  return `${sign}${Math.round(abs)}`;
}
